import React from "react";
import { Badge } from "@/components/ui/badge";
import { useAdminUsers } from "@/lib/hooks/use-api";
import type { User } from "@/lib/types/user";
import { UserDisplayPopover } from "@/components/shared/UserDisplayPopover";

interface ExpandedMultiUserDisplayProps {
  assignedUsers?: string[];
  assignedUser?: any; // For backward compatibility with single assigned_user
}

export const ExpandedMultiUserDisplay: React.FC<ExpandedMultiUserDisplayProps> = ({
  assignedUsers = [],
  assignedUser,
}) => {
  const { data: allUsers } = useAdminUsers();

  // Combine assigned_users array with legacy assigned_user
  const allAssignedUserIds = React.useMemo(() => {
    const userIds = new Set<string>();

    assignedUsers.forEach((userId) => {
      if (userId && userId.trim()) {
        userIds.add(userId);
      }
    });

    if (assignedUser?.id && !userIds.has(assignedUser.id)) {
      userIds.add(assignedUser.id);
    }

    return Array.from(userIds);
  }, [assignedUsers, assignedUser]);

  const assignedUserDetails = React.useMemo(() => {
    if (!allUsers || allAssignedUserIds.length === 0) return [];

    return allAssignedUserIds
      .map((userId) => {
        const user = allUsers.find((u: User) => u.id === userId);
        if (!user) return null;

        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
        const displayName = fullName || user.email || "Unknown";
        return {
          id: user.id,
          name: displayName,
          email: user.email ?? "",
          initials: (user.first_name?.[0] || "") + (user.last_name?.[0] || ""),
          isActive: user.is_active,
        };
      })
      .filter((user): user is NonNullable<typeof user> => user !== null);
  }, [allUsers, allAssignedUserIds]);

  if (assignedUserDetails.length === 0) {
    return <div className="text-gray-400 text-sm">No users assigned</div>;
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Assigned to {assignedUserDetails.length} user{assignedUserDetails.length !== 1 ? "s" : ""}
      </div>
      <div className="space-y-2">
        {assignedUserDetails.map((user) => {
          const fullUser = allUsers?.find((u: User) => u.id === user.id);

          if (!fullUser) {
            return (
              <div key={user.id} className="flex items-center gap-3 p-2 bg-card-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    {user.initials || user.name[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-card-foreground truncate">{user.name}</div>
                  <div className="text-sm text-gray-500 truncate">{user.email}</div>
                </div>
                {!user.isActive && (
                  <Badge variant="secondary" className="text-xs">
                    Inactive
                  </Badge>
                )}
              </div>
            );
          }

          return (
            <UserDisplayPopover key={user.id} user={fullUser}>
              <div className="flex items-center gap-3 p-2 bg-card-50 rounded-lg cursor-pointer hover:bg-card-100 transition-colors">
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    {user.initials || user.name[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-card-foreground truncate">{user.name}</div>
                  <div className="text-sm text-gray-500 truncate">{user.email}</div>
                </div>
                {!user.isActive && (
                  <Badge variant="secondary" className="text-xs">
                    Inactive
                  </Badge>
                )}
              </div>
            </UserDisplayPopover>
          );
        })}
      </div>
    </div>
  );
};
